import { useTranslation } from 'react-i18next';
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  useToast,
} from '@org/ui';
import { Share2, Copy, MessageCircle, Twitter, Facebook, Instagram } from 'lucide-react';

interface ShareMenuProps {
  url: string;
  title: string;
  text?: string;
}

export function ShareMenu({ url, title, text }: ShareMenuProps) {
  const { t } = useTranslation('community');
  const { toast } = useToast();

  const shareText = text ? `${title} — ${text}` : title;

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(url);
    toast({ title: t('share.linkCopied') });
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title, text: text ?? title, url });
      } catch {
        // User cancelled — not an error
      }
    }
  };

  const handleWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleInstagram = async () => {
    await navigator.clipboard.writeText(url);
    toast({ title: t('share.linkCopiedInstagram') });
  };

  const supportsNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5"
          aria-label={t('post.share')}
          onClick={(e) => {
            if (supportsNativeShare) {
              e.preventDefault();
              handleNativeShare();
            }
          }}
        >
          <Share2 className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={handleCopyLink}>
          <Copy className="mr-2 h-4 w-4" />
          {t('share.copyLink')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleWhatsApp}>
          <MessageCircle className="mr-2 h-4 w-4" />
          {t('share.whatsapp')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleTwitter}>
          <Twitter className="mr-2 h-4 w-4" />
          {t('share.twitter')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleFacebook}>
          <Facebook className="mr-2 h-4 w-4" />
          {t('share.facebook')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleInstagram}>
          <Instagram className="mr-2 h-4 w-4" />
          {t('share.instagram')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
